import WidgetCard from "@/components/cards/widget-card";
import { useElementSize } from "@/hooks/use-element-size";
import { Order } from "@/types";
import { DatePicker } from "@/ui/datepicker";
import cn from "@/utils/class-names";
import { set } from "lodash";
import { c } from "node_modules/vite/dist/node/types.d-aGj9QkWt";
import { useEffect, useState } from "react";
import WorldMap from "react-svg-worldmap";
import { Badge, Text } from "rizzui";

type ContryMap = {
    country: string;
    name: string;
    value: number;
    style: string;
};

export default function TopShipmentCountries({
    orders,
    className,
}: {
    orders: Order[];
    className?: string;
}) {
    const [countriesMap, setCountriesMap] = useState<ContryMap[]>([]);

    const countryNames = {
        SOM: "Somaliland",
        KE: "Kenya",
        UG: "Uganda",
        TZ: "Tanzania",
        RW: "Rwanda",
        BI: "Burundi",
        SS: "South Sudan",
        ET: "Ethiopia",
        SO: "Somalia",
        DJ: "Djibouti",
        ER: "Eritrea",
    };

    useEffect(() => {
        if (orders && Array.isArray(orders)) {
            const updatedCountriesMap: ContryMap[] = [];

            orders.forEach((order) => {
                if (order.shippingAddress) {
                    const countryISOTwo = order.country; // Ensure this is correct
                    const countryName = countryNames[countryISOTwo ?? ""];
                    const countryIndex = updatedCountriesMap.findIndex(
                        (country) => country.country === countryISOTwo,
                    );

                    if (countryIndex === -1) {
                        updatedCountriesMap.push({
                            country: countryISOTwo ?? "",
                            name: countryName,
                            value: orders.map((order) => order.country).length,
                            style: "bg-[#028ca6]",
                        });
                    } else {
                        updatedCountriesMap[countryIndex].value += 1;
                    }
                }
            });

            setCountriesMap(updatedCountriesMap);
        }
    }, [orders]);

    const [ref, { width }] = useElementSize();
    const [startDate, setStartDate] = useState<Date>(new Date());
    return (
        <WidgetCard
            title="Top Countries"
            action={
                <DatePicker
                    selected={startDate}
                    onChange={(date: Date | null) => setStartDate(date!)}
                    dateFormat="MMM, yyyy"
                    placeholderText="Select Month"
                    showMonthYearPicker
                    popperPlacement="bottom-end"
                    inputProps={{
                        variant: "text",
                        inputClassName: "p-0 px-1 h-auto",
                    }}
                    className="w-36"
                    maxDate={new Date()}
                />
            }
            headerClassName="col-span-full flex-col gap-2 @md:flex-row"
            actionClassName="ps-0"
            className={cn(
                "relative grid grid-cols-1 place-content-between gap-3",
                className,
            )}
        >
            <div
                ref={ref}
                className="col-span-full [&_figure]:!bg-transparent [&_svg]:dark:invert"
            >
                <WorldMap
                    color="#028ca6"
                    valueSuffix="%"
                    size={width}
                    data={countriesMap}
                />
            </div>

            <div className="col-span-full -mx-5 border-t border-dashed border-muted px-5 pt-5 lg:-mx-7 lg:px-7">
                <div className="mx-auto flex w-full max-w-md flex-wrap justify-center gap-x-3 gap-y-1.5 text-center">
                    {countriesMap.map((country) => (
                        <div
                            key={country.name}
                            className="flex items-center gap-1"
                        >
                            {country.name && (
                                <>
                                    <Badge
                                        renderAsDot
                                        className={cn(
                                            country.style,
                                            "dark:invert",
                                        )}
                                    />
                                    <Text className="text-gray-500 dark:text-gray-600">
                                        {country.name}
                                    </Text>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </WidgetCard>
    );
}
